import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { RiskBadge } from "../../components/ui/RiskBadge.js";
import { ScoreBar } from "../../components/ui/ScoreBar.js";

interface EmployeeRow {
  id: number;
  displayName: string;
  department: string | null;
  retentionScore: number | null;
  riskLevel: "high" | "medium" | "low" | null;
  topSignals: string[];
  // Optional wie in api.ts (EmployeeRecord.isConfirmedLeaving?)
  isConfirmedLeaving?: boolean;
}

interface TrendData {
  available: boolean;
  since?: string;
  points: { date: string; avgScore: number; highRiskCount: number }[];
}

export function TrendAndRisks(props: {
  trend?: TrendData;
  employees: EmployeeRow[];
  loading: boolean;
  onSelectEmployee: (id: number) => void;
}) {
  const { t } = useTranslation();
  // Spec Entscheidung 5: ab mittlerem Risiko (Score >= 45 %), absteigend, ohne
  // bestätigte Abgänge — Liste trägt auch ohne rote Fälle.
  const risks = props.employees
    .filter((e) => (e.riskLevel === "high" || e.riskLevel === "medium") && !e.isConfirmedLeaving)
    .sort((a, b) => (b.retentionScore ?? 0) - (a.retentionScore ?? 0));

  return (
    <div className="grid grid-cols-2 gap-4 mb-5">
      <div className="card p-5">
        <div
          className="text-[11px] font-bold tracking-widest uppercase mb-1"
          style={{ color: "var(--text-muted)" }}
        >
          {t("cockpit.trend.title")}
        </div>
        <div className="text-[10px] mb-2" style={{ color: "var(--text-muted)" }}>
          {t("cockpit.trend.subtitle")}
        </div>
        {props.trend?.available ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={props.trend.points}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "var(--text-muted)" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(d: string) =>
                  new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
                }
              />
              <YAxis
                tick={{ fontSize: 10, fill: "var(--text-muted)" }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                domain={[0, "auto"]}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  boxShadow: "var(--shadow-md)",
                  fontSize: 12,
                }}
                labelFormatter={(d) => new Date(String(d)).toLocaleDateString("de-DE")}
                formatter={(v: number) => [`${v}`, t("cockpit.trend.tooltip")]}
              />
              <Line
                type="monotone"
                dataKey="highRiskCount"
                stroke="var(--risk-high)"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            {t("cockpit.trend.building", { since: props.trend?.since ?? t("cockpit.trend.today") })}
          </p>
        )}
      </div>

      <div className="card p-5">
        <div
          className="text-[11px] font-bold tracking-widest uppercase mb-1"
          style={{ color: "var(--text-muted)" }}
        >
          {t("cockpit.risks.title")}
        </div>
        <div className="text-[10px] mb-2" style={{ color: "var(--text-muted)" }}>
          {t("cockpit.risks.subtitle")}
        </div>
        {props.loading ? (
          <div className="flex flex-col gap-2 py-2" aria-hidden>
            {["r1", "r2", "r3"].map((k) => (
              <div key={k} className="skeleton h-10" />
            ))}
          </div>
        ) : risks.length === 0 ? (
          <p className="text-xs py-4 text-center" style={{ color: "var(--text-muted)" }}>
            {t("cockpit.risks.empty")}
          </p>
        ) : (
          <div className="flex flex-col max-h-[260px] overflow-y-auto pr-1">
            {risks.map((emp) => (
              <button
                key={emp.id}
                type="button"
                onClick={() => props.onSelectEmployee(emp.id)}
                className="flex items-center gap-3 py-2 px-1 -mx-1 rounded text-left row-hover"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span
                      className="font-mono-data text-xs font-medium truncate"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {emp.displayName}
                    </span>
                    {emp.riskLevel && <RiskBadge level={emp.riskLevel} />}
                  </div>
                  <p className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                    {emp.department ?? "—"}
                    {emp.topSignals[0] ? ` · ${emp.topSignals[0]}` : ""}
                  </p>
                </div>
                {emp.retentionScore !== null && emp.riskLevel && (
                  <ScoreBar score={emp.retentionScore} riskLevel={emp.riskLevel} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
